import { CSSProperties } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";

// Components
import ButtonSymphony from "elements/ButtonSymphony";
import Loader from "elements/Loader";
import CampaignImageSymphony from "elements/CampaignImageSymphony";
import TagSymphony from "elements/TagSymphony";
import BrandInfo from "elements/BrandInfo";
import Recommendations from "components/shareable/Recommendations";

// Hooks
import useScreen from "Hooks/useScreen";

// Utils & Constants
import {
  getCampaignTypeInfo,
  getFallbackCampaignName,
  makeKey,
} from "modules/Utils";
import {
  CAMPAIGN_STATUS_INFO,
  CampaignStatus,
  PlaybookType,
} from "modules/Const";
import { toastSuccess } from "modules/Toast";
import Sty from "modules/Sty";

// Types
import { CampaignMetadata, CurrentBrand } from "modules/Types";

interface CampaignAnalytics {
  assetPerformanceAnalytics?: {
    stats?: {
      cpc?: number;
      cpv?: number;
    };
  };
  followers?: number;
  views?: number;
  totalOfPresaves?: number;
  [key: string]: any; // Allow additional properties for flexibility
}

interface CampaignData {
  id: number;
  status: CampaignStatus;
  campaign_metadata: CampaignMetadata;
  analytics: CampaignAnalytics;
  createdAt: string;
  brandInfo?: CurrentBrand;
}

interface CampaignTypeStat {
  statTitle: string;
  statValue: string | number;
}

interface CampaignsSidebarProps {
  /** Array of campaigns to display */
  campaigns: CampaignData[];
  /** Whether campaigns are currently loading */
  isLoading: boolean;
  /** Title for the sidebar */
  title?: string;
  /** Additional class name */
  className?: string;
  /** Whether to show brand info for each campaign (for organization view) */
  showBrandInfo?: boolean;
  /** Whether to show recommendations when no campaigns */
  showRecommendations?: boolean;
}

const CampaignsSidebar = ({
  campaigns,
  isLoading,
  title,
  className = "",
  showBrandInfo = false,
  showRecommendations = true,
}: CampaignsSidebarProps) => {
  const history = useHistory();
  const { mobileView, mStyle } = useScreen();

  const getDaysAgo = (createdAt: string) => {
    const daysAgo = dayjs().diff(dayjs(createdAt), "days");

    if (daysAgo <= 7) {
      if (daysAgo === 0) {
        return "today";
      }
      return `${daysAgo} days ago`;
    }

    return dayjs(createdAt).format("MMM D, YYYY");
  };

  const getCampaignTypeStat = (
    campaignType: string,
    analytics: CampaignAnalytics
  ): CampaignTypeStat => {
    switch (campaignType) {
      case PlaybookType.INCREASE_LINK_CLICKS:
        return {
          statTitle: "Cost per click",
          statValue: analytics?.assetPerformanceAnalytics?.stats?.cpc || 0,
        };
      case PlaybookType.INCREASE_MUSIC_STREAMS:
        return {
          statTitle: "Cost per click",
          statValue: analytics?.assetPerformanceAnalytics?.stats?.cpc || 0,
        };
      case PlaybookType.BOOST_INSTAGRAM_ENGAGEMENT:
        return {
          statTitle: "Instagram Followers",
          statValue: analytics?.followers || 0,
        };
      case PlaybookType.INCREASE_VIDEO_VIEWS:
        return {
          statTitle: "Cost per view",
          statValue: analytics?.assetPerformanceAnalytics?.stats?.cpv || 0,
        };
      case PlaybookType.TIKTOK_BOOST_ENGAGEMENT:
        return {
          statTitle: "TikTok Views",
          statValue: analytics?.views || 0,
        };
      case PlaybookType.META_PRESAVES:
        return {
          statTitle: "Pre-saves",
          statValue: analytics?.totalOfPresaves || 0,
        };
      default:
        return {
          statTitle: "Unknown",
          statValue: "Unknown",
        };
    }
  };

  const handleCopyLink = (campaignId: number) => {
    navigator.clipboard.writeText(
      `${window.location.origin}/marketing/campaigns/${campaignId}`
    );
    toastSuccess("Link copied to clipboard");
  };

  const handleViewCampaign = (campaignId: number) => {
    history.push(`/marketing/campaigns/${campaignId}`);
  };

  const hasNoCampaigns = campaigns.length === 0;
  const displayTitle =
    title || (hasNoCampaigns ? "Get Started" : "Recent Campaigns");

  return (
    <div className={`campaignsSidebar ${className}`}>
      <div
        className="campaignsSidebarTitle"
        style={{
          ...Sty.text18,
          paddingBottom: 8,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div className="campaignsSidebarTitleText">{displayTitle}</div>
        {isLoading && <Loader className="campaignsSidebarLoader" />}
      </div>
      <div
        className="campaignsSidebarContainer"
        style={mStyle(sty, "campaignsSidebarContainer")}
      >
        {hasNoCampaigns && showRecommendations && (
          <Recommendations isRecommdationVertical />
        )}

        {campaigns.map((campaign, index) => {
          const { status, campaign_metadata, analytics, createdAt, brandInfo } =
            campaign;

          const { content, campaign_type, playbook, v2_campaign_input } =
            campaign_metadata || {};

          const campaignType: string = playbook || campaign_type || "Unknown";
          const campaignStatus: CampaignStatus =
            status || CampaignStatus.UNKNOWN;

          const { statTitle, statValue }: CampaignTypeStat =
            getCampaignTypeStat(campaignType, analytics);

          return (
            <div
              key={makeKey(campaign.id, index)}
              className="campaignCard"
              style={mStyle(sty, "campaignCard")}
            >
              <CampaignImageSymphony
                className="campaignCardThumbnail"
                style={{
                  width: "100%",
                  height: mobileView ? 50 : 100,
                  borderRadius: 5,
                }}
                campaignMetadata={campaign_metadata as any}
                isBackgroundImage={true}
                iconSize={60}
              />

              {showBrandInfo && brandInfo && (
                <BrandInfo
                  className="campaignCardBrandInfo"
                  brandName={brandInfo.name}
                  brandImage={brandInfo.image || undefined}
                  brandSlug={brandInfo.slug ? brandInfo.slug : undefined}
                  variant="small"
                  titleColor={Sty.text12grey.color}
                />
              )}

              <div
                className="campaignCardTitle"
                style={
                  mobileView ? Sty.text12grey : { ...Sty.text16, marginTop: 2 }
                }
              >
                {getFallbackCampaignName(campaign_metadata)}
              </div>
              <div className="campaignCardSubtitle" style={Sty.text14grey}>
                Created {getDaysAgo(createdAt)}
              </div>

              <TagSymphony
                className="campaignTag"
                icon={getCampaignTypeInfo(campaignType as any).icon}
                title={getCampaignTypeInfo(campaignType as any).title}
                type="square"
                size={mobileView ? "small" : "medium"}
              />
              <TagSymphony
                className="campaignTag"
                title={CAMPAIGN_STATUS_INFO[campaignStatus].title}
                color={CAMPAIGN_STATUS_INFO[campaignStatus].color}
                type="square"
                size={mobileView ? "small" : "medium"}
              />
              <div
                className="campaignButtonsContainer"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  width: "100%",
                }}
              >
                <ButtonSymphony
                  className="campaignCopyButton"
                  onClick={() => handleCopyLink(campaign.id)}
                  variant="text"
                  size={mobileView ? "small" : "medium"}
                  iconRight="link2"
                  iconRightSize={mobileView ? 12 : 16}
                  iconRightOffset={-2}
                  tooltip="Copy to clipboard"
                >
                  Copy Link
                </ButtonSymphony>
                <ButtonSymphony
                  className="campaignViewButton"
                  variant="text"
                  size={mobileView ? "small" : "medium"}
                  iconRight="arrowRight"
                  iconRightSize={mobileView ? 16 : 20}
                  iconRightOffset={-8}
                  onClick={() => handleViewCampaign(campaign.id)}
                >
                  View
                </ButtonSymphony>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const sty: Record<string, CSSProperties> = {
  campaignsSidebarContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    overflow: "scroll",
  },
  campaignsSidebarContainer_medium: {
    flexDirection: "row",
    overflow: "scroll",
  },
  campaignCard: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 8,
    backgroundColor: "white",
    borderRadius: 8,
    alignItems: "flex-start",
    width: "100%",
    flexShrink: 0,
    scrollbarWidth: "none",
    border: Sty.border,
    overflow: 'hidden',
  },
  campaignCard_medium: {
    width: 160,
    padding: 4,
  },
};

/**
 * Adapter to convert various campaign data formats to CampaignsSidebar compatible format
 * Works with both CampaignDetails and OrganizationCampaignData
 */
export const adaptCampaignsForSidebar = <T extends Record<string, any>>(
  campaigns: T[]
): CampaignData[] => {
  return campaigns
    .filter((campaign) => campaign.status !== CampaignStatus.DRAFT)
    .map((campaign) => ({
      id: campaign.id,
      status: (campaign.status as CampaignStatus) || CampaignStatus.UNKNOWN,
      campaign_metadata: campaign.campaign_metadata || {},
      analytics: campaign.analytics || {},
      createdAt:
        campaign.createdAt || campaign.created_at || new Date().toISOString(),
      brandInfo: campaign.brandInfo || campaign.brand_info || undefined,
    }));
};

// Export types for external use
export type { CampaignData, CampaignAnalytics };

export default CampaignsSidebar;
